import * as React from 'react';
import { styled } from '@mui/material/styles';
import { Box, IconButton, ListItem, Typography } from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { useTranslation } from 'react-i18next';

import { IItemReadResourceV10 } from 'components/basket/model/Basket';
import {
    getModifierProductsString,
    getModifierProductsTotal,
    getProductOptionsDetails
} from 'components/menu/model/Menu';
import { IUserReadResourceV12 } from 'components/order/model/Order';
import { addOpacity } from 'lib/helpers';
import { isDefined, isEmptyString } from 'lib/typeInference';
import { useCurrencyString } from 'lib/useCurrencyString';
import { UserAvatar } from 'lib/UserAvatar';
import { RecursiveModifierProductList } from './RecursiveModifierProducts';

const PREFIX = 'ViewBillOrderItem';

const classes = {
    productQuantity: `${PREFIX}productQuantity`,
    productInfo: `${PREFIX}productInfo`,
    productModifiers: `${PREFIX}productModifiers`,
    productPrice: `${PREFIX}productPrice`,
    avatar: `${PREFIX}avatar`
};

const StyledListItem = styled(ListItem)(({ theme }) => ({
    display: 'flex',
    width: '100%',
    flexShrink: 0,
    alignItems: 'flex-start',
    [`& .${classes.productQuantity}`]: {
        minWidth: '10%',
        marginRight: theme.spacing(0.5)
    },

    [`& .${classes.productInfo}`]: {
        flex: 1
    },

    [`& .${classes.productModifiers}`]: {
        color: addOpacity(theme.palette.text.primary, 0.6)
    },

    [`& .${classes.productPrice}`]: {
        minWidth: '20%',
        textAlign: 'right'
    },

    [`& .${classes.avatar}`]: {
        width: '32px',
        height: '32px'
    }
}));

interface IProps {
    orderItem: IItemReadResourceV10;
    index?: number;
    onClick?: (item: IItemReadResourceV10) => void;
    showClaimedBy?: boolean;
    user?: IUserReadResourceV12;
    showPricePerOne?: boolean;
}

export const ViewBillOrderItem: React.FC<React.PropsWithChildren<IProps>> = ({
    orderItem,
    showClaimedBy,
    index,
    onClick,
    showPricePerOne,
    user
}) => {
    const getCurrencyString = useCurrencyString();
    const { t } = useTranslation();

    const modifierProducts = React.useMemo(() => orderItem.children || [], [orderItem.children]);

    const [modifierOptionsString, calculatedOptionsPrice] = React.useMemo(
        () => getProductOptionsDetails(orderItem.modifiers),
        [orderItem.modifiers]
    );
    const hasNestedModifiers = React.useMemo(
        () =>
            modifierProducts.some(
                modifierProduct => modifierProduct.children && modifierProduct.children.length > 0
            ),
        [modifierProducts]
    );
    const [showMore, setShowMore] = React.useState(false);

    const modifierProductsTotal = React.useMemo(
        () => getModifierProductsTotal(modifierProducts, orderItem.quantity) + orderItem.cost,
        [modifierProducts, orderItem.quantity, orderItem.cost]
    );

    const modifierProductsString = React.useMemo(() => {
        const string = getModifierProductsString(modifierProducts, orderItem.quantity);
        return string.length > 100 && hasNestedModifiers ? string.slice(0, 100) + '...' : string;
    }, [modifierProducts, orderItem.quantity, hasNestedModifiers]);

    const handleShowMore = React.useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
        event.stopPropagation();
        event.preventDefault();
        setShowMore(value => !value);
    }, []);

    const totalPrice = React.useMemo(
        () => Math.round((modifierProductsTotal + calculatedOptionsPrice) * orderItem.quantity * 100) / 100,
        [modifierProductsTotal, calculatedOptionsPrice, orderItem.quantity]
    );

    const handleClick = React.useCallback(() => {
        if (isDefined(onClick) && isDefined(index)) {
            onClick(orderItem);
        }
    }, [onClick, index, orderItem]);

    if (orderItem.parentReferenceId) {
        return null;
    }

    return (
        <StyledListItem onClick={handleClick} disableGutters>
            {showClaimedBy ? (
                <Box marginRight={0.5}>
                    <UserAvatar user={user} classNames={classes.avatar} />
                </Box>
            ) : (
                <Typography className={classes.productQuantity}>
                    {!showPricePerOne ? orderItem.quantity : 1}
                    {'x'}
                </Typography>
            )}
            <Box className={classes.productInfo}>
                <Typography>{orderItem.productName}</Typography>
                {!isEmptyString(modifierOptionsString) && (
                    <Typography variant="caption" className={classes.productModifiers}>
                        {modifierOptionsString}
                    </Typography>
                )}
                {!showMore && (
                    <Typography variant="caption" className={classes.productModifiers}>
                        {modifierProductsString}
                    </Typography>
                )}
                {showMore && (
                    <RecursiveModifierProductList
                        modifierProducts={modifierProducts}
                        orderItemQuantity={orderItem.quantity}
                    />
                )}

                {hasNestedModifiers && (
                    <IconButton sx={{ paddingY: 0 }} size="small" color="primary" onClick={handleShowMore}>
                        {showMore ? (
                            <Box display="flex" alignItems="center" gap={0.5}>
                                <KeyboardArrowUpIcon fontSize="small" />{' '}
                                <Typography variant="caption" className={classes.productModifiers}>
                                    {t('NESTED_MODIFIERS_SHOW_LESS')}
                                </Typography>
                            </Box>
                        ) : (
                            <Box display="flex" alignItems="center" gap={0.5}>
                                <KeyboardArrowDownIcon fontSize="small" />{' '}
                                <Typography variant="caption" className={classes.productModifiers}>
                                    {t('NESTED_MODIFIERS_SHOW_MORE')}
                                </Typography>
                            </Box>
                        )}
                    </IconButton>
                )}
            </Box>
            <Typography className={classes.productPrice}>
                {getCurrencyString(showPricePerOne ? totalPrice / orderItem.quantity : totalPrice)}
            </Typography>
        </StyledListItem>
    );
};
